import {
  listTeams,
  listTenantMembers,
  Team,
  TeamDisplayData,
  TenantMember,
  TenantMemberDisplayData,
} from '@kudos/http-client'
import { FIRST_PAGE, SEARCH_PAGE_SIZE } from '@shared/filters/dynamicSelectUtils'

export type IdentitySearchPage<ResultT> = {
  results: ResultT[]
  /** The page number to ask for next, or null once the list is exhausted. */
  next: number | null
}

const toTeamDisplayData = (team: Team): TeamDisplayData => ({
  id: team.id,
  name: team.name,
  segmentType: team.segmentType ?? null,
  teamType: team.teamType,
  activeMemberCount: team.activeMemberCount ?? 0,
})

const toMemberDisplayData = (member: TenantMember): TenantMemberDisplayData => ({
  id: member.id,
  email: member.email ?? null,
  firstName: member.firstName,
  lastName: member.lastName,
  active: !member.suspendedAt,
  avatarUrls: member.avatarUrls ?? null,
  jobTitle: member.publicStaffProfile?.jobTitle ?? '',
})

/**
 * These pickers page the list endpoints rather than the cursor search the filter
 * trays use. The search endpoints return only an id and a name, so each page
 * needs a second lookup for avatars, team type, and member counts — and that
 * lookup takes at most 20 ids at a time, so a page of 50 arrives as several
 * requests. The list endpoints embed the display data, so one page of options
 * costs one request.
 */
export const listTeamsDisplayPage = async ({
  search,
  page,
  hidden,
  automated,
}: {
  search?: string
  page?: number
  hidden?: boolean
  automated?: boolean
}): Promise<IdentitySearchPage<TeamDisplayData>> => {
  const listed = await listTeams({
    page: page ?? FIRST_PAGE,
    pageSize: SEARCH_PAGE_SIZE,
    search,
    hidden,
    automated,
    sorting: ['+name'],
    embeds: ['types', 'activeMemberCount'],
  })
  return {
    results: listed.results.map(toTeamDisplayData),
    next: listed.next,
  }
}

export const listMembersDisplayPage = async ({
  search,
  page,
  suspended = false,
}: {
  search?: string
  page?: number
  suspended?: boolean
}): Promise<IdentitySearchPage<TenantMemberDisplayData>> => {
  const listed = await listTenantMembers({
    page: page ?? FIRST_PAGE,
    pageSize: SEARCH_PAGE_SIZE,
    search,
    suspended,
    sorting: ['+name'],
    embeds: ['email', 'avatar'],
  })
  return {
    results: listed.results.map(toMemberDisplayData),
    next: listed.next,
  }
}
